import { createTheme } from "@mui/material/styles";
import darkScrollbar from '@mui/material/darkScrollbar';
export const AE_palette = {
  charcoal: '#2C2C2C',
  melonRed: '#FF455C',
  silver: '#E8E8E8',
  white: '#FFFFFF',
  seafoamGreen: '#45FFA7',
  seafoamGreen85: '#45FFA785',
  orchidPurple: '#C445FF',
  amber: '#FEEB66',
  activeOrange: '#FCC144',
  savedBlue: '#4EC2F5',
};

/**
 * AE design system, applied centrally.
 *
 * The colour grammar is semantic, not decorative — get the MEANING right and the
 * shade follows:
 *
 *   Yellow #FCC144  REQUIRED ACTION. Every primary CTA (Save, New, Add, Create,
 *                   Confirm, Import). Yellow background, BLACK text.
 *   Blue   #4EC2F5  "You are here" / passive highlight — active tab, focused
 *                   input, current step, a ticked checkbox. NEVER a CTA.
 *   Green  #45FFA7  Success / confirmed action / positive status.
 *   Amber  #FEEB66  Warning — a possible issue, not yet an error.
 *   Red    #FF455C  Error / critical.
 *   Purple #C445FF  Informational notice, where none of the above fit.
 *
 * WHY primary MOVED OFF #61676b. That grey rendered contained buttons at 2.13:1
 * against the #323638 dialog paper, while MUI's DISABLED background sits at
 * 1.34:1 — enabled and disabled were the same object to the eye, and every text
 * button failed WCAG AA outright. Operators read the whole app as greyed out.
 * Black on #FCC144 is 12.85:1; blue text on paper is 6.05:1.
 *
 * NOT adopted here (deliberate): the AE "yellow border at rest" rule for input
 * fields. 14 of the 25 files containing form fields hand-roll their own
 * notchedOutline colours, so adopting it centrally would light up half the app
 * and leave the other half grey — louder AND less coherent than today. Focus is
 * already savedBlue, which is the half of the field rule that matters most.
 * Take fields as their own pass, together with those 14 files.
 */
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      // REQUIRED ACTION. contrastText is pinned rather than left to
      // augmentColor: MUI would compute rgba(0,0,0,0.87), which is legible but
      // is not the spec, and white on this yellow is 1.63:1 — unreadable. Any
      // future edit that drops this line silently risks white-on-yellow.
      main: AE_palette.activeOrange,
      contrastText: '#000000',
    },
    secondary: {
      // "You are here" / secondary action. Deliberately NOT used for CTAs.
      main: AE_palette.savedBlue,
      contrastText: '#000000',
    },
    background: {
      default: "#1A1A1A",
      paper: "#323638",
    },
    text: {
      primary: AE_palette.white,
    },
    info: {
      main: AE_palette.orchidPurple,
      contrastText: '#000000',
    },
    success: {
      main: AE_palette.seafoamGreen,
      contrastText: '#000000',
    },
    warning: {
      // Amber, NOT activeOrange. If warnings were the same yellow as primary
      // CTAs the yellow would stop meaning "press me" — the signal only works
      // while it is reserved.
      main: AE_palette.amber,
      contrastText: '#000000',
    },
    error: {
      main: AE_palette.melonRed,
    },
    // Custom keys. MUI only augments the six standard palette entries, so these
    // get no light/dark/contrastText — which is why components using
    // color="seafoamGreen" hand-roll their hover. Kept because live call sites
    // depend on them; do not add more.
    seafoamGreen: {
      main: AE_palette.seafoamGreen,
    },
    seafoamGreen85: {
      main: AE_palette.seafoamGreen85,
    },
    amber: {
      main: AE_palette.amber,
      contrastText: '#000',
    },
  },
  typography: {
    fontFamily: '"Barlow", sans-serif',
    fontSize: 16,
    h1: {
      fontSize: "2rem",
      fontWeight: 600,
    },
    h2: {
      fontSize: "1.75rem",
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
       styleOverrides: (themeParam) => ({
        body: themeParam.palette.mode === 'dark' ? darkScrollbar() : null,
        a: {
          color: AE_palette.melonRed,
          textDecoration: 'none',
          '&:visited': {
            color: AE_palette.seafoamGreen,
          },
          '&:hover': {
            color: AE_palette.seafoamGreen,
            textDecoration: 'underline',
          },
        },
       }),
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.5)',
          '&.Mui-focused': {
            color: AE_palette.savedBlue,
          },
          '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.2)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.25)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: AE_palette.savedBlue,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.08)',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          // No focus-visible styling existed anywhere, and the browser default
          // ring is hard to see on a dark surface — worse against yellow.
          '&:focus-visible': {
            outline: `2px solid ${AE_palette.savedBlue}`,
            outlineOffset: 2,
          },
        },
        // REQUIRED ACTION.
        containedPrimary: {
          backgroundColor: AE_palette.activeOrange,
          color: '#000000',
          // Brighter on hover, not darker. MUI's default reaches for
          // palette.primary.dark, which on a yellow CTA reads as "going away".
          '&:hover': { backgroundColor: '#FFD166' },
          '&:active': { backgroundColor: '#F0B233' },
          // Explicit colours, never `opacity`: opacity compounds unpredictably
          // over whatever surface the button happens to sit on. This has to be
          // unmistakably NOT the enabled state — that confusion is the bug.
          '&.Mui-disabled': {
            backgroundColor: '#4A4F52',
            color: 'rgba(255,255,255,0.45)',
          },
        },
        // SECONDARY action — blue outline that fills on hover.
        outlinedPrimary: {
          color: AE_palette.savedBlue,
          borderColor: 'rgba(78,194,245,0.55)',
          '&:hover': {
            backgroundColor: AE_palette.savedBlue,
            borderColor: AE_palette.savedBlue,
            color: '#000000',
          },
          '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.35)',
            borderColor: 'rgba(255,255,255,0.15)',
          },
        },
        // Cancel / Back / Close / Clear are dismissals, not calls to action, so
        // they must not be yellow. Neutral and legible: 7.4:1 on the dialog
        // paper, against the 2.13:1 (AA-failing) grey they had.
        textPrimary: {
          color: 'rgba(255,255,255,0.78)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.06)',
            color: '#FFFFFF',
          },
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.32)' },
        },
      },
    },
    // A ticked checkbox is STATE ("you have set this"), not an action — blue.
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: 'rgba(255,255,255,0.45)',
          '&.Mui-checked': { color: AE_palette.savedBlue },
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.18)' },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        colorPrimary: {
          color: 'rgba(255,255,255,0.45)',
          '&.Mui-checked': { color: AE_palette.savedBlue },
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.18)' },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // colorPrimary lands on switchBase, so the track needs the sibling.
        colorPrimary: {
          '&.Mui-checked': { color: AE_palette.savedBlue },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: AE_palette.savedBlue,
            opacity: 0.55,
          },
        },
      },
    },
    // The clearest statement of the colour grammar in the app: the step you are
    // ON is blue ("you are here"), the steps you have FINISHED are green
    // ("confirmed"). Using blue for both would collapse the two states and lose
    // the one job blue has.
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.25)',
          '&.Mui-active': { color: AE_palette.savedBlue },
          '&.Mui-completed': { color: AE_palette.seafoamGreen },
        },
        text: { fill: '#000000', fontWeight: 600 },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: 'rgba(255,255,255,0.55)',
          '&.Mui-active': { color: '#FFFFFF', fontWeight: 600 },
          '&.Mui-completed': { color: 'rgba(255,255,255,0.75)' },
        },
      },
    },
    // Active tab = "you are here" = blue. Note the app's one <Tabs> instance
    // (TeamFixturePage) overrides the indicator locally, so this is groundwork
    // for the next one rather than an immediate visual change.
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: AE_palette.savedBlue, height: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,0.6)',
          '&.Mui-selected': { color: '#FFFFFF' },
        },
      },
    },
  },
});

export default darkTheme;